import { convertToFlat, normalizeVehicles } from './normalizer';

const API_END = process.env.VUE_APP_MOZIO_API_ENDPOINT;
const API_KEY = process.env.VUE_APP_MOZIO_API_KEY;

/**
 * We recommend calling this endpoint approximately every 1-2 seconds for 15 seconds,
 * although the vast majority of our search results will be available in 5-10 seconds.
 */
const MOZIO_POLL_DELAY_TIME = 1500;
const MOZIO_POLL_MAX_TIME = 15000;

const BASE_SEARCH_PARAMS = {
  /** Possible modes are round_trip o one_way */
  mode: 'one_way',
  /** Don't enable polling for the results */
  no_pull: false,
};

const CUSTOM_HEADERS_PROVIDERS = [];

const getStartSearchUrl = () => '/search/';
const getPollSearchUrl = searchId => `/search/${searchId}/poll/`;
const getVehiclesUrl = () => '/vehicles/';

const getProtectedHeader = () => ({
  'API-KEY': API_KEY,
});

export const setCustomRequestHeadersProvider = fn => CUSTOM_HEADERS_PROVIDERS.push(fn);

const httpCall = async (url, method, params = {}, headers = {}) => {
  const response = await fetch(`${API_END}${url}`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...getProtectedHeader(),
      ...headers,
      ...CUSTOM_HEADERS_PROVIDERS.reduce((acc, next) => ({ ...acc, ...next() }), {}),
    },
    method,
    body: JSON.stringify(params.body),
  });
  const json = await response.json();
  return [json, response.status];
};

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const pollSearch = async (searchId) => {
  const [{ more_coming: isHasMore, results }] = await httpCall(getPollSearchUrl(searchId), 'GET');
  return { isHasMore, results };
};

const pollCycle = async (fn, onSuccessHandler, step = 1) => {
  const isAvailableStep = step * MOZIO_POLL_DELAY_TIME < MOZIO_POLL_MAX_TIME;
  if (!isAvailableStep) {
    return Promise.resolve(true);
  }
  await delay(MOZIO_POLL_DELAY_TIME);
  const { isHasMore, results } = await fn();
  onSuccessHandler(results);
  if (isHasMore) {
    return pollCycle(fn, onSuccessHandler, step + 1);
  }
  return Promise.resolve(true);
};

export const startSearch = async (params) => {
  const body = {
    ...params,
    ...BASE_SEARCH_PARAMS,
  };
  const [result, code] = await httpCall(getStartSearchUrl(), 'POST', { body });
  if (code === 400) {
    throw new Error();
  }
  return result;
};

export const startPollSearch = async (searchId, onSuccessResponse) => {
  const pollSearchCb = () => pollSearch(searchId);
  const onSuccessResponseWrapper = (results) => {
    if (results.length) {
      onSuccessResponse(convertToFlat(results));
    }
  };
  return pollCycle(pollSearchCb, onSuccessResponseWrapper);
};

export const fetchVehicles = async () => {
  const [vehicles] = await httpCall(getVehiclesUrl(), 'GET');
  return normalizeVehicles(vehicles);
};
